import React from 'react';
import { useTranslation } from 'react-i18next';
import type { StructuredResultItem } from '../types';
import { formatDistance } from '../utils';

interface EventResultItemRendererProps {
  item: StructuredResultItem;
}

export const EventResultItemRenderer: React.FC<EventResultItemRendererProps> = ({ item }) => {
  const { t } = useTranslation();

  const getEventDetails = () => {
    const details = [];
    
    if (item.event_date) details.push({
      content: `📅 ${item.event_date}`,
      testId: 'event-date'
    });
    if (item.event_time) details.push({
      content: `🕐 ${item.event_time}`,
      testId: 'event-time'
    });
    if (item.venue) details.push({
      content: `🏢 ${item.venue}`,
      testId: 'event-venue'
    });
    
    // Common location info
    details.push({
      content: `📍 ${item.location}`,
      testId: 'location'
    });
    if (item.distance_from_user !== undefined && item.distance_from_user !== null) {
      details.push({
        content: `📏 ${formatDistance(item.distance_from_user)} km ${t('results.distance.away')}`,
        testId: 'distance-from-user'
      });
    }
    
    return details;
  };

  return (
    <article className="border rounded p-3 bg-purple-50 text-sm border-purple-100" data-testid="event-item">
      {/* Title and Badge */}
      <div className="flex items-start justify-between mb-2">
        <h5 className="font-semibold text-gray-900 leading-tight flex-1 pr-2" data-testid="item-title">
          {item.title}
        </h5>
        <span 
          className="px-2 py-0.5 rounded text-xs font-medium shrink-0 bg-purple-100 text-purple-800 border border-purple-200"
          data-testid="event-badge"
          role="status"
          aria-label="Event"
        >
          {t('results.contentTypes.event')}
        </span>
      </div>
      
      {/* Event-specific details */}
      <div className="flex flex-wrap gap-4 mb-2 text-xs text-gray-600" data-testid="event-details">
        {getEventDetails().map((detail, index) => (
          <span key={index} data-testid={detail.testId}>
            {detail.content}
          </span>
        ))}
      </div>
      
      {/* Organizer info if available */}
      {item.organizer && (
        <p className="text-xs text-purple-700 mb-2" data-testid="event-organizer">
          👥 Organized by {item.organizer}
        </p>
      )}
      
      {/* Description */}
      <p className="text-gray-700 mb-2 text-sm leading-tight" data-testid="item-description">
        {item.description}
      </p>
      
      {/* Link */}
      <a 
        href={item.url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="inline-flex items-center text-purple-600 hover:text-purple-800 text-xs font-medium"
        data-testid="visit-website-link"
        aria-label={`Visit website for ${item.title}`}
      >
        🔗 {t('results.actions.visitWebsite')}
      </a>
    </article>
  );
};